/**
 * tile-personalizer.js — personalize product cards with the shopper's own pet.
 *
 * For any card the storefront marked personalizable (`[data-tile-personalize]`),
 * if the user already has a clean AI image for THAT card's style, composite the
 * pet onto the product's default-variant base mockup (via window.PetComposite)
 * and swap it into the card. Everything is progressive enhancement over the
 * server-rendered plain image — any failure silently leaves the plain image.
 *
 * Two entry points (see the tile-personalization spec, "Orchestration & timing"):
 *   - personalize(root)            → load-time path (catalog / home; image already exists)
 *   - the `p2a:design-ready` event → upsell path (image generated during the visit)
 *
 * Never displays the bare base mockup: base images load into PetComposite's
 * offscreen cache, and the card's <img> is only ever swapped to the FINISHED
 * composite.
 *
 * Exposed as window.TilePersonalizer.
 */
(function () {
  "use strict";

  const BACKEND_URL =
    (/(^|\.)(instame\.shop|lilaland\.co|lilaland\.pet)$/i.test(location.hostname) || /\.myshopify\.com$/i.test(location.hostname))
      ? "https://api.instame.shop"
      : (location.hostname === "localhost" || location.hostname === "127.0.0.1")
        ? "http://localhost:3000" // direct `shopify theme dev` → local backend
        : ""; // other non-prod hosts (Tailscale/phone) → same-origin dev gateway

  const MAX_CONCURRENT = 3; // simultaneous base-image loads + composites
  const IO_ROOT_MARGIN = "300px"; // pre-warm just below the fold
  const RETRY_MAX_ATTEMPTS = 20; // upsell: wait for the just-generated clean image
  const RETRY_INTERVAL_MS = 2500; // 20 × 2.5s = 50s: "-opaque" background removal can outlast the old 15s budget
  const FETCH_TIMEOUT_MS = 8000; // per-attempt cap so a hung request can't wedge a pass
  const FETCH_RETRY_ATTEMPTS = 3;
  const FETCH_RETRY_BACKOFF_MS = [1000, 3000]; // wait before attempt 2, attempt 3

  // product_id -> tiles-config entry (or null when not personalizable)
  const configCache = new Map();
  /** @type {Record<string, {image_url: string, pet_name: string, user_upload_id?: string}> | null} */
  let stylesMemo = null;
  let stylesPromise = null;

  // ── small utils ──────────────────────────────────────────────────────

  function getUserId() {
    try {
      return localStorage.getItem("instame_user_id") || null;
    } catch {
      return null;
    }
  }

  // PostHog's replay session id (a UUIDv7 minted by posthog-js). Guarded:
  // before posthog-js finishes loading, the stub's get_session_id returns
  // undefined and the event simply goes unlinked.
  function phSessionId() {
    try {
      if (window.posthog && typeof window.posthog.get_session_id === "function") {
        const s = window.posthog.get_session_id();
        if (typeof s === "string" && s) {
          return s;
        }
      }
    } catch (_) {}
    return null;
  }

  // Fire-and-forget PostHog event via the backend proxy — same path + shape as
  // instant-preview's telemetry. Used for error pings and the personalized-click
  // signal. `keepalive` so a click event survives the ensuing navigation.
  // $session_id links the server-captured event to the session replay.
  function track(event, properties) {
    if (isHillsdale()) return;
    try {
      const userId = getUserId();
      if (!userId) return;
      const props = Object.assign({}, properties || {});
      const replaySessionId = phSessionId();
      if (replaySessionId) {
        props["$session_id"] = replaySessionId;
      }
      fetch(`${BACKEND_URL}/api/events/posthog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, event, properties: props }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) {}
  }

  // Clean rows are stored under the transparent style name (e.g. "headshot-modern"),
  // which is also what products reference. A card whose style is the "-opaque"
  // display variant normalizes to the same key.
  function normalizeStyle(s) {
    return (s || "").trim().toLowerCase().replace(/-opaque$/, "");
  }

  function saveData() {
    try {
      const c = navigator.connection;
      return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || "")));
    } catch {
      return false;
    }
  }

  function stylesCacheKey(userId) {
    return `instame_user_styles_${userId}`;
  }

  // ── data fetches ─────────────────────────────────────────────────────

  function isHillsdale() {
    return !!document.querySelector('meta[name="hillsdale-tile-proxy"]') ||
      /^(hillsdale\.lilaland\.co|h9pw2h-fy\.myshopify\.com)$/i.test(location.hostname);
  }
  function styleIdentity(style) { return style?.image_identity || style?.image_url; }
  function currentStyle(style) {
    return !isHillsdale() || !!window.HillsdaleVisitor?.isCurrentStyle?.(style);
  }

  function tilesConfigurationUrl() {
    const config = document.querySelector('meta[name="hillsdale-tile-proxy"]');
    const hillsdaleHost = /^(hillsdale\.lilaland\.co|h9pw2h-fy\.myshopify\.com)$/i.test(location.hostname);
    if (config) {
      // Liquid supplies the canonical shop even on a Shopify preview hostname.
      // Browser settings select transport only: Shopify signs the proxy request.
      if (config.dataset.shop !== "h9pw2h-fy.myshopify.com" || config.dataset.enabled !== "true") return null;
      const prefix = config.dataset.prefix || "";
      if (!/^\/(apps|a|community|tools)\/[a-z0-9][a-z0-9_-]{0,29}$/.test(prefix) ||
          ["admin", "services", "password", "login"].includes(prefix.split("/")[2])) return null;
      return `${prefix}/preview/tiles-config`;
    }
    // Missing/off/malformed Hillsdale configuration never falls back to the
    // legacy direct route. Other stores retain their original public lookup.
    return hillsdaleHost ? null : `${BACKEND_URL}/api/preview/tiles-config`;
  }

  /**
   * fetch with a per-attempt timeout and bounded retries for TRANSIENT
   * failures only: network errors (including the timeout abort) and 5xx —
   * the windows a backend deploy/restart opens. 4xx is returned unretried.
   * Resolves { res, attempts } (res may still be non-ok on 4xx, or 5xx on
   * the last attempt); rejects with the final network error, which carries
   * an `attempts` count for tracking.
   */
  async function fetchWithRetry(url, init) {
    for (let attempt = 1; ; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(url, Object.assign({}, init, { signal: controller.signal }));
        if (res.status < 500 || attempt >= FETCH_RETRY_ATTEMPTS) {
          return { res, attempts: attempt };
        }
      } catch (err) {
        if (attempt >= FETCH_RETRY_ATTEMPTS) {
          if (err && typeof err === "object") {
            err.attempts = attempt;
          }
          throw err;
        }
      } finally {
        clearTimeout(timer);
      }
      await new Promise((resolve) =>
        setTimeout(resolve, FETCH_RETRY_BACKOFF_MS[attempt - 1] || FETCH_RETRY_BACKOFF_MS[FETCH_RETRY_BACKOFF_MS.length - 1]),
      );
    }
  }

  async function fetchUserStyles(opts) {
    if (isHillsdale()) {
      // Never bridge the legacy user ID/cache into the new private identity.
      stylesMemo = window.HillsdaleVisitor ? await window.HillsdaleVisitor.getStyles(opts) : {};
      return stylesMemo;
    }
    const force = !!(opts && opts.force);
    const userId = getUserId();
    if (!userId) return {};

    if (!force && stylesMemo) return stylesMemo;

    if (!force) {
      try {
        const cached = sessionStorage.getItem(stylesCacheKey(userId));
        if (cached) {
          stylesMemo = JSON.parse(cached);
          return stylesMemo;
        }
      } catch {}
    }

    if (!force && stylesPromise) return stylesPromise;

    stylesPromise = (async () => {
      let attempts = FETCH_RETRY_ATTEMPTS;
      try {
        const result = await fetchWithRetry(
          `${BACKEND_URL}/api/user/styles?user_id=${encodeURIComponent(userId)}`,
        );
        attempts = result.attempts;
        const res = result.res;
        if (!res.ok) {
          track("tile_personalize_error", { phase: "styles_fetch_http", status: res.status, attempts });
          return stylesMemo || {};
        }
        const data = await res.json();
        stylesMemo = data.styles || {};
        try {
          sessionStorage.setItem(stylesCacheKey(userId), JSON.stringify(stylesMemo));
        } catch {}
        return stylesMemo;
      } catch (err) {
        if (err && err.attempts) {
          attempts = err.attempts;
        }
        track("tile_personalize_error", { phase: "styles_fetch", message: String((err && err.message) || err), attempts });
        return stylesMemo || {};
      } finally {
        stylesPromise = null;
      }
    })();

    return stylesPromise;
  }

  async function fetchTilesConfig(cards) {
    const configUrl = tilesConfigurationUrl();
    if (!configUrl) return;
    const need = cards.filter((c) => !configCache.has(c.dataset.productId || cardProductId(c)));
    if (need.length === 0) return;

    const products = need.map((card) => {
      let variantOptions = [];
      try {
        variantOptions = JSON.parse(card.dataset.variantOptions || "[]");
      } catch {}
      return {
        shopify_product_id: cardProductId(card),
        base_template_slug: card.dataset.baseTemplateSlug || "",
        variant_options: variantOptions,
      };
    });

    let attempts = FETCH_RETRY_ATTEMPTS;
    try {
      const result = await fetchWithRetry(configUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ products }),
      });
      attempts = result.attempts;
      const res = result.res;
      if (!res.ok) {
        track("tile_personalize_error", { phase: "config_fetch_http", status: res.status, attempts });
        return;
      }
      const data = await res.json();
      const configs = data.configs || {};
      for (const card of need) {
        const id = cardProductId(card);
        configCache.set(id, configs[id] || null);
      }
    } catch (err) {
      if (err && err.attempts) {
        attempts = err.attempts;
      }
      track("tile_personalize_error", { phase: "config_fetch", message: String((err && err.message) || err), attempts });
      // leave configs unset; cards stay plain
    }
  }

  // ── card helpers ─────────────────────────────────────────────────────

  function cardProductId(card) {
    return card.getAttribute("data-product-id") || card.dataset.productId || "";
  }

  function collectCards(root) {
    const scope = root || document;
    return Array.from(scope.querySelectorAll("[data-tile-personalize]"));
  }

  function cardImage(card) {
    return (
      card.querySelector(".card-gallery img") ||
      card.querySelector(".product-card__image img") ||
      card.querySelector("img")
    );
  }

  // When the P2A page has an active pet (the upload the shopper is currently
  // working with), tiles must only personalize with THAT pet's images — a
  // style generated with an earlier pet stays/reverts to the plain mockup.
  // Null on catalog/home pages, where "latest image per style" is still the
  // wanted behavior. Set via setPetContext / the p2a:design-ready event.
  let contextUploadId = null;

  function setPetContext(uploadId) {
    contextUploadId = uploadId || null;
  }

  function matchStyle(card, styles) {
    const key = normalizeStyle(card.dataset.petStyle);
    const style = key ? styles[key] || null : null;
    if (style && contextUploadId && style.user_upload_id !== contextUploadId) {
      return null;
    }
    return style;
  }

  // ── compositing + swap ───────────────────────────────────────────────

  async function personalizeCard(card, style) {
    if (!currentStyle(style)) return;
    if (card.dataset.tilePersonalizing) return;
    // Skip if already personalized with this exact image — allows re-personalization
    // when a new P2A image is generated (stable job identity for private images).
    if (card.dataset.tilePersonalized === styleIdentity(style)) return;
    const id = cardProductId(card);
    const cfg = configCache.get(id);
    if (!cfg) return; // no base image / template → stay plain
    const img = cardImage(card);
    if (!img) return;

    card.dataset.tilePersonalizing = "1";
    try {
      // Step 1: composite the pet photo onto the product mockup.
      const canvas = await window.PetComposite.composite({
        baseImageUrl: cfg.base_image_url,
        // Composite from the compressed display copy when the backend provides
        // one — the output is downsampled to card size anyway. image_url stays
        // the legacy identity key; private reads supply a stable job identity.
        aiImageUrl: style.display_image_url || style.image_url,
        template: cfg.template,
        printArea: cfg.print_area,
        petName: style.pet_name,
        needpetname: card.dataset.petNeedpetname === "true",
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      if (!currentStyle(style)) { revertCard(card); return; }

      // Step 2: pre-decode the data URL. The browser decodes it into its image
      // cache so the swap in step 4 renders instantly, with no gap between
      // fade-out completing and the new image appearing.
      await new Promise((resolve) => {
        const preload = new Image();
        preload.onload = resolve;
        preload.onerror = resolve; // don't block the swap on a decode error
        preload.src = dataUrl;
      });

      // Step 3: fade out the current (default) image.
      img.style.transition = "opacity 0.15s ease";
      img.style.opacity = "0";
      await new Promise((r) => setTimeout(r, 150)); // wait for fade-out to finish

      // Step 4: swap src while the image is invisible. Keep the plain
      if (!currentStyle(style)) { revertCard(card); return; }
      // mockup's src/srcset so a later pet-context change can revert the
      // tile (only the first swap records them — after that img.src is our
      // own data URL, not the mockup).
      if (!card.dataset.tileOriginalSrc) {
        card.dataset.tileOriginalSrc = img.getAttribute("src") || "";
        card.dataset.tileOriginalSrcset = img.getAttribute("srcset") || "";
      }
      img.removeAttribute("srcset");
      img.src = dataUrl;
      card.dataset.tilePersonalized = styleIdentity(style);
      // Stamp the pet this image belongs to. The shown style may be from an
      // OLDER generation for a different pet than the one just used, so the
      // upsell-click prefill reads these to keep the product page's pre-selected
      // photo + name matching the picture, not the latest session pet.
      card.dataset.tileUploadId = style.user_upload_id || "";
      card.dataset.tilePetName = style.pet_name || "";

      // Step 5: fade in the personalized image (handled in finally below).
    } catch (err) {
      // Silent fallback — the plain image is already on screen.
      console.warn("[tile-personalizer] composite failed", cardProductId(card), err);
      track("tile_personalize_error", {
        phase: "composite",
        product_id: cardProductId(card),
        pet_style: card.dataset.petStyle || null,
        message: String((err && err.message) || err),
      });
    } finally {
      // Step 5 (success) / restore (failure): fade back in. On failure the
      // opacity was never changed so this is a no-op transition to 1.
      delete card.dataset.tilePersonalizing;
      img.style.transition = "opacity 0.4s ease";
      img.style.opacity = "1";
    }
  }

  // Concurrency-limited runner over a list of {card, style}.
  async function processQueue(jobs) {
    let i = 0;
    async function worker() {
      while (i < jobs.length) {
        const job = jobs[i++];
        await personalizeCard(job.card, job.style);
      }
    }
    const workers = [];
    for (let w = 0; w < Math.min(MAX_CONCURRENT, jobs.length); w++) {
      workers.push(worker());
    }
    await Promise.all(workers);
  }

  // ── viewport-gated scheduling ────────────────────────────────────────

  let observer = null;
  const pending = new Map(); // card -> style

  function ensureObserver() {
    if (observer || typeof IntersectionObserver === "undefined") return;
    observer = new IntersectionObserver(
      (entries) => {
        const ready = [];
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const card = entry.target;
          const style = pending.get(card);
          observer.unobserve(card);
          pending.delete(card);
          if (style) ready.push({ card, style });
        }
        if (ready.length) processQueue(ready);
      },
      { rootMargin: IO_ROOT_MARGIN },
    );
  }

  function schedule(card, style) {
    if (card.dataset.tilePersonalized === styleIdentity(style) || pending.has(card)) return;
    if (typeof IntersectionObserver === "undefined") {
      // No IO support → personalize immediately (rare).
      processQueue([{ card, style }]);
      return;
    }
    ensureObserver();
    pending.set(card, style);
    observer.observe(card);
  }

  // ── public flows ─────────────────────────────────────────────────────

  // One pass: match collectable cards against the user's styles and schedule
  // the matches. Returns the number of cards newly scheduled/personalized.
  // A personalized tile whose style no longer matches (the shopper switched
  // to a different pet) goes back to the plain mockup rather than keep
  // showing the wrong pet.
  function revertCard(card) {
    if (!card.dataset.tilePersonalized) return;
    const img = cardImage(card);
    if (img && card.dataset.tileOriginalSrc) {
      img.src = card.dataset.tileOriginalSrc;
      if (card.dataset.tileOriginalSrcset) {
        img.setAttribute("srcset", card.dataset.tileOriginalSrcset);
      }
      img.style.opacity = "";
    }
    delete card.dataset.tilePersonalized;
    delete card.dataset.tileUploadId;
    delete card.dataset.tilePetName;
  }

  async function runPass(root, opts) {
    if (saveData()) return 0;
    const cards = collectCards(root);
    if (!cards.length) return 0;

    const styles = await fetchUserStyles(opts);
    const matched = cards
      .map((card) => ({ card, style: matchStyle(card, styles) }))
      .filter((m) => m.style);

    // Revert tiles personalized with a pet the context now excludes.
    for (const card of cards) {
      if (
        card.dataset.tilePersonalized &&
        ((isHillsdale() && !currentStyle(matchStyle(card,styles))) ||
         (contextUploadId && card.dataset.tileUploadId !== contextUploadId && !pending.has(card)))
      ) {
        if (isHillsdale()) { pending.delete(card); observer?.unobserve(card); }
        revertCard(card);
      }
    }
    if (!matched.length) return 0;

    await fetchTilesConfig(matched.map((m) => m.card));

    let scheduled = 0;
    for (const { card, style } of matched) {
      if (card.dataset.tilePersonalized === styleIdentity(style) || pending.has(card)) continue;
      if (configCache.get(cardProductId(card))) {
        schedule(card, style);
        scheduled++;
      }
    }
    return scheduled;
  }

  // Load-time path (catalog / home): a single pass.
  async function personalize(root) {
    try {
      await runPass(root || document, { force: false });
    } catch (err) {
      console.warn("[tile-personalizer] personalize failed", err);
    }
  }

  // Pre-warm the user-independent config (one cheap batched JSON call) so the
  // design-ready composite has everything but the AI image ready. Base mockups
  // are intentionally NOT decoded here — they download lazily at composite time,
  // so off-style tiles that never personalize never cost the shopper any data.
  // Safe to call before any AI image exists.
  async function prewarm(root) {
    try {
      const cards = collectCards(root);
      if (cards.length) await fetchTilesConfig(cards);
    } catch {}
  }

  // Upsell path: the AI image flips to ready during the visit. Re-check the
  // user's styles (force-refetch) until the expected style appears, compositing
  // matches as they become available. Bounded — one tiny JSON call per attempt.
  async function personalizeWithRetry(root, expectedStyle, expectedUploadId) {
    const wantKey = normalizeStyle(expectedStyle);
    if (expectedUploadId) {
      setPetContext(expectedUploadId);
    }
    // Snapshot the image identity before retrying so we can detect when the backend
    // has actually written the *new* image — not just returned the previously
    // cached one from an earlier generation.
    const prevImageIdentity = wantKey && stylesMemo ? styleIdentity(stylesMemo[wantKey]) : undefined;

    for (let attempt = 0; attempt < RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        await runPass(root || document, { force: true });
      } catch (err) {
        console.warn("[tile-personalizer] retry pass failed", err);
      }
      // Stop when: no specific style expected, OR the style now exists with a
      // different image identity than before the retry started (new image landed)
      // AND — when a pet context is expected — it belongs to that pet, so an
      // older pet's image for the same style can't satisfy the wait.
      const entry = wantKey && stylesMemo ? stylesMemo[wantKey] : undefined;
      const currentIdentity = styleIdentity(entry);
      const uploadOk = !expectedUploadId || entry?.user_upload_id === expectedUploadId;
      if (!wantKey || (currentIdentity && currentIdentity !== prevImageIdentity && uploadOk)) break;
      await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS));
    }
  }

  // ── auto-wiring ──────────────────────────────────────────────────────

  // The photo-to-art funnel dispatches this when generation completes. The
  // clean image may land slightly after the watermarked result (background
  // removal runs last for "-opaque" styles), so we retry-poll for it.
  document.addEventListener("p2a:design-ready", (e) => {
    const styleName = e && e.detail && e.detail.styleName;
    const uploadId = e && e.detail && e.detail.uploadId;
    personalizeWithRetry(document, styleName, uploadId);
  });

  // Personalized-click signal: when a product card is clicked, record whether the
  // tile was actually personalized (showing the shopper's pet). Lets us compare
  // click-through of personalized vs. plain tiles. Capture phase + keepalive so it
  // lands before navigation; scoped to product links so unrelated clicks are ignored.
  document.addEventListener(
    "click",
    (e) => {
      try {
        const link = e.target.closest && e.target.closest("a[href]");
        if (!link) return;
        if (!/\/products\//.test(link.getAttribute("href") || "")) return;
        const card = link.closest("[data-product-id]");
        if (!card) return;
        track("tile_product_click", {
          product_id: cardProductId(card),
          personalized: !!card.dataset.tilePersonalized,
          personalizable: card.hasAttribute("data-tile-personalize"),
          pet_style: card.dataset.petStyle || null,
        });
      } catch (_) {}
    },
    true,
  );

  window.TilePersonalizer = {
    personalize,
    personalizeWithRetry,
    prewarm,
    setPetContext,
    // exposed for tests
    _normalizeStyle: normalizeStyle,
    _fetchUserStyles: fetchUserStyles,
    _matchStyle: matchStyle,
    _revertCard: revertCard,
  };
})();
